import {normalizeCode} from "./identity.js";
import {numberText} from "./sanitize.js";

// Talks to the /auth endpoints and turns their answers into something the lobby can
// render. Signing in is optional: a guest still types a name and plays, they just get
// no history. Every failure here therefore degrades to "not signed in" rather than
// blocking the game — a party must not stop because the auth service is down.
export const ANONYMOUS = Object.freeze({
  available: false,
  authenticated: false,
  user: null,
  stats: null,
  recentGames: [],
  deck: null,
  liveGame: null,
});

function anonymous(available) {
  return { ...ANONYMOUS, available: Boolean(available) };
}

export function normalizeAccount(payload) {
  if (!payload || typeof payload !== "object") return ANONYMOUS;
  if (!payload.authenticated) return anonymous(payload.available);

  return {
    available: true,
    authenticated: true,
    user: payload.user || null,
    stats: payload.stats || null,
    recentGames: Array.isArray(payload.recentGames) ? payload.recentGames : [],
    deck: normalizeDeck(payload.deck),
    liveGame: normalizeLiveGame(payload.liveGame),
  };
}

// The seat this account can reconnect to, as the server found it. The code goes through
// the same normalizer as a typed one, and a seat without a full code or a player id is
// no seat at all.
function normalizeLiveGame(liveGame) {
  const code = normalizeCode(liveGame?.code ?? "");
  const playerId = liveGame?.playerId;
  if (code.length !== 5 || typeof playerId !== "string" || !playerId) return null;
  return { code, playerId, status: liveGame.status === "lobby" ? "lobby" : "playing" };
}

// The deck saved on the account, as the server stored it. Only the shape is fixed
// here; whether the ids make a legal deck is deckRules' question, asked by the
// deckbuilder before saving and by the host before dealing.
function normalizeDeck(deck) {
  if (!Array.isArray(deck?.cards)) return null;
  const ids = (list) => list.filter((id) => typeof id === "string");
  return { cards: ids(deck.cards), openingHand: Array.isArray(deck.openingHand) ? ids(deck.openingHand) : [] };
}

export function loginUrl(returnTo = "/") {
  return `/auth/login?returnTo=${encodeURIComponent(returnTo)}`;
}

// The two counter fragments the signed-in line and the leaderboard both print. They
// live here so the copy cannot drift between the two surfaces, and so every counter
// crosses numberText on exactly one path — a missing stat reads "0", never "undefined".
function winRecordText(won, played) {
  return `${numberText(won)}/${numberText(played)} vinster`;
}

function sipsText(sips) {
  return `${numberText(sips)} klunkar`;
}

// Everything the lobby's account block paints: the sign-in pitch a guest sees above
// the join form, or the signed-in player's name and record. Shaped here rather than
// in JSX so the copy stays under test. The name comes from the auth service, not from
// a synced game state, but it is still text from outside this device — JSX escapes it.
export function accountPanel(account) {
  const name = accountName(account);
  if (!account.authenticated) {
    return {
      available: Boolean(account.available),
      authenticated: false,
      name: "",
      title: "Logga in för att spara din statistik",
      subtitle: "Namnet följer med mellan dina enheter.",
    };
  }

  const stats = account.stats;
  return {
    available: true,
    authenticated: true,
    // "" for an account with neither name nor email: the lobby then keeps its name
    // field rather than seating someone under a blank.
    name,
    title: name || "Inloggad",
    subtitle: stats
      ? `${winRecordText(stats.games_won, stats.games_played)} · ${sipsText(stats.total_sips)}`
      : "Inga matcher räknade än.",
  };
}

// The name a signed-in player plays under, or "" for a guest — which is also how the
// lobby decides whether to show a name field at all. Falling back to the email keeps
// an account without a display name from turning into an anonymous seat.
export function accountName(account) {
  if (!account.authenticated) return "";
  return (account.user?.name || account.user?.email || "").trim();
}

// What the lobby's name field starts out as. The account wins: once you sign in the
// lobby stops offering a field, so the account name is the identity, and it follows
// you to a phone that has never played here. A guest keeps this device's name.
// Callers hold a normalized account and a string, so neither is re-guarded here.
export function preferredName(account, savedName) {
  return accountName(account) || savedName.trim();
}

// Swedish ordinals: 1:a, 2:a, 3:e … and 11:e/12:e, which break the pattern.
function ordinal(value) {
  const lastTwo = value % 100;
  const lastOne = value % 10;
  const suffix = (lastOne === 1 || lastOne === 2) && lastTwo !== 11 && lastTwo !== 12 ? ":a" : ":e";
  return `${value}${suffix}`;
}

export function placementText(placement, playerCount) {
  const place = Number(placement);
  if (!Number.isInteger(place) || place < 1) return "Ingen placering";
  if (place === 1) return "Vinst";

  const total = Number(playerCount);
  const field = Number.isInteger(total) && total > 0 ? ` av ${total}` : "";
  return `${ordinal(place)} plats${field}`;
}

// One row per past game for the lobby's history panel. The numbers arrive from the
// statistics tables rather than from a synced game state, but they still cross a
// network, so every one of them goes through numberText before it is rendered.
// normalizeAccount already guarantees a normalized account with an array here, so
// only the signed-in check remains.
export function recentGameRows(account) {
  if (!account.authenticated) return [];

  return account.recentGames.map((game, index) => {
    // The same normalizer every other lobby code in the browser goes through, so a
    // code out of the database cannot render characters a live code never could.
    const code = normalizeCode(game?.code || "") || "?????";
    const place = Number(game?.placement);
    // game_players.survived is `not null default true`, so only an explicit false
    // means someone was knocked out; anything else reads as the column's default.
    const survived = game?.survived !== false;
    return {
      key: `${index}-${code}`,
      code,
      outcome: placementText(place, game?.player_count),
      detail: `${sipsText(game?.sips_taken)} · ${survived ? "överlevde" : "utslagen"}`,
      won: place === 1,
    };
  });
}

const LEADERBOARD_SIZE = 5;

// The all-time board for the host screen. Names come from the auth service, so they
// are text from outside this device — JSX escapes them, and nothing here builds HTML.
export function leaderboardRows(entries) {
  if (!Array.isArray(entries)) return [];

  return entries.slice(0, LEADERBOARD_SIZE).map((entry, index) => ({
    // Keyed on position: the board is public, so it carries no user id to key on.
    key: String(index),
    rank: index + 1,
    name: String(entry?.name || "").trim() || "Okänd spelare",
    record: winRecordText(entry?.games_won, entry?.games_played),
    sips: sipsText(entry?.total_sips),
  }));
}

export class AccountClient {
  constructor(fetchImpl) {
    this.fetchImpl = fetchImpl || ((...args) => fetch(...args));
  }

  async #post(path, body) {
    const response = await this.fetchImpl(path, {
      method: "POST",
      headers: { "content-type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(body),
    });
    if (!response.ok) return null;
    return response.json();
  }

  // Every read degrades the same way: a failure is null, and each caller turns that
  // into the empty answer its surface should show rather than throwing at the party.
  async #get(path) {
    try {
      const response = await this.fetchImpl(path, { credentials: "same-origin" });
      if (!response.ok) return null;
      return await response.json();
    } catch {
      return null;
    }
  }

  async load() {
    const payload = await this.#get("/auth/me");
    return payload ? normalizeAccount(payload) : ANONYMOUS;
  }

  // Binds this device's player id to the signed-in account so the games it plays are
  // attributed. The server will not take a user id from the game state itself.
  async link(playerId) {
    if (!playerId) return false;
    try {
      const result = await this.#post("/auth/link", { playerId });
      return Boolean(result?.linked);
    } catch {
      return false;
    }
  }

  // Saves the lobby's deckbuilder draft on the account. Null on any failure, so the
  // builder can say the save did not go through instead of throwing at the party.
  async saveDeck(deck) {
    try {
      const result = await this.#post("/auth/deck", deck);
      return normalizeDeck(result?.deck);
    } catch {
      return null;
    }
  }

  // The leaderboard is public and needs no session: without a database the server
  // answers with an empty board, and so does every failure here. This returns the
  // rows as data — shaping them for the screen is the controller's job, exactly as
  // it is for the history panel.
  async leaderboard() {
    const payload = await this.#get("/api/stats/leaderboard");
    return Array.isArray(payload?.leaderboard) ? payload.leaderboard : [];
  }

  async logout() {
    try {
      await this.#post("/auth/logout", {});
    } catch {
      // Nothing useful to do: the cookie is cleared by the response we did not get,
      // so reloading the account below reports whatever the truth turned out to be.
    }
    return this.load();
  }
}
